import { createContext, useContext, useState, ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import CustomButton from "../components/customs/custom-button";
import CustomSelect from "../components/customs/custom-select";
import ModalPortal from "../components/modal-portal";
import type { ComboboxOption } from "../types";

const confirmationModelData = [
  {
    type: "delete",
    title: "Are you sure you want to Delete?",
    desc: "Once you deleted you cannot undo this action",
    button: "Delete",
  },
  {
    type: "confirmation",
    title: "Are you sure you want to Procced?",
    desc: "Once you confirmed you cannot undo this action",
    button: "Confirm",
  },
];

/** An answer the action needs before it can run, e.g. the lapse reason. */
export type ConfirmSelect = {
  label: string;
  placeholder?: string;
  /** Fixed choices — a `number` field has none. */
  options?: ComboboxOption[];
  /** How the answer is collected. Defaults to a select. */
  type?: "select" | "number" | "text";
  /** Pre-filled answer, e.g. the count the chase is already on. */
  defaultValue?: string;
  /** The patch key the picked value is sent under. */
  field: string;
  required?: boolean;
};

interface IConfirmationModel {
  /** Gets the picked value when the modal carries a `select`. */
  onClick: (value?: string) => void;
  type: "delete" | "confirmation";
  loading?: boolean;
  /** Per-action copy — say what will happen, not just "proceed". */
  title?: string;
  desc?: string;
  /** Amber warning under the description, for side effects worth a second look. */
  alert?: string;
  /** Confirm-button label, e.g. "Archive". Cancel always sits beside it. */
  button?: string;
  /** Red confirm button — for the ones that undo work. */
  danger?: boolean;
  select?: ConfirmSelect;
}

type DeleteContextType = {
  showModal: (options: IConfirmationModel) => void;
  hideModal: () => void;
};

const ConfirmationContext = createContext<DeleteContextType | undefined>(
  undefined
);

export const useConfirmationModel = () => {
  const context = useContext(ConfirmationContext);
  return context!;
};

export const ConfirmationModelProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<IConfirmationModel | null>(null);
  // The `select` answer, and the "you have to pick one" nudge.
  const [choice, setChoice] = useState<ComboboxOption | null>(null);
  const [error, setError] = useState("");
  const modelData = confirmationModelData?.find(
    (cmd) => cmd.type === data?.type
  );

  // Anything the caller passes wins over the generic copy for its type.
  const copy = {
    title: data?.title ?? modelData?.title,
    desc: data?.desc ?? modelData?.desc,
    button: data?.button ?? modelData?.button,
    danger: data?.danger ?? data?.type === "delete",
  };

  const showModal = (options: IConfirmationModel) => {
    setData(options);
    // Seeded so confirming without touching the field keeps the current value.
    setChoice(
      options.select?.defaultValue
        ? { value: options.select.defaultValue }
        : null
    );
    setError("");
    setVisible(true);
  };

  const hideModal = () => {
    setVisible(false);
    setData(null);
    setChoice(null);
    setError("");
  };

  return (
    <ConfirmationContext.Provider value={{ showModal, hideModal }}>
      {children}
      {visible && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center backdrop-blur-md bg-black/50 animate-fade-in p-4"
            onClick={hideModal}
          >
            <div
              className="bg-surface-elevated border border-edge p-6 rounded-2xl shadow-overlay w-full max-w-[500px] transition-all animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-2 text-text-primary">
                {copy.title}
              </h2>
              <p className="mb-4 text-light-gray">{copy.desc}</p>
              {data?.alert && (
                <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3.5 py-3 text-[13px] text-amber-600">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="mt-0.5 shrink-0"
                  />
                  <span>{data.alert}</span>
                </div>
              )}
              {data?.select?.type === "number" ||
              data?.select?.type === "text" ? (
                <div className="space-y-1">
                  <label className="text-label-muted font-medium block text-sm leading-[16.98px]">
                    {data.select.label}
                  </label>
                  <input
                    type={data.select.type}
                    min={0}
                    step={1}
                    inputMode={
                      data.select.type === "number" ? "numeric" : "text"
                    }
                    autoFocus
                    className={clsx(
                      "w-full py-2 px-3 text-sm text-text-input font-bold tabular-nums border border-input-border rounded-lg bg-input-bg transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30",
                      error && "border-red-500"
                    )}
                    placeholder={data.select.placeholder}
                    defaultValue={data.select.defaultValue}
                    onChange={(e) => {
                      // A count — whole and never negative; text goes as typed.
                      setChoice({
                        value:
                          data.select.type === "text"
                            ? e.target.value.trim()
                            : String(
                                Math.max(
                                  0,
                                  Math.floor(Number(e.target.value) || 0)
                                )
                              ),
                      });
                      setError("");
                    }}
                  />
                  {error ? (
                    <p className="text-red-400 text-xs font-medium">{error}</p>
                  ) : null}
                </div>
              ) : data?.select ? (
                <CustomSelect
                  comboboxType="select"
                  label={data.select.label}
                  placeholder={data.select.placeholder}
                  options={data.select.options}
                  valueProperty="value"
                  labelProperty="label"
                  value={choice ?? undefined}
                  error={error}
                  onSingleSelectChange={(option) => {
                    setChoice(option);
                    setError("");
                  }}
                />
              ) : null}
              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <CustomButton
                  disabled={data?.loading}
                  onClick={hideModal}
                  className="order-2 sm:order-1 border border-edge-strong text-text-primary px-6 py-2 rounded-lg hover:bg-overlay transition cursor-pointer h-[46px] text-md font-semibold"
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  disabled={data?.loading}
                  loading={data?.loading}
                  onClick={() => {
                    if (data?.select?.required && !choice?.value)
                      return setError(`${data.select.label} is required`);
                    setData((prev) => ({ ...prev, loading: true }));
                    data?.onClick(choice?.value);
                  }}
                  className={clsx(
                    "order-1 sm:order-2 text-white px-6 py-2 rounded-lg transition cursor-pointer h-[46px] text-md font-semibold",
                    copy.danger
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-accent hover:bg-accent-bright"
                  )}
                >
                  {copy.button}
                </CustomButton>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </ConfirmationContext.Provider>
  );
};
